import { Fragment, useEffect, useState } from 'react';
import { isEqual, isPlainObject } from 'lodash';
import { Box, Button, CircularProgress } from '@mui/material';
import { AddRounded as AddIcon } from '@mui/icons-material';
import {
  useEditorStore,
  useGetEditorModel,
  ContextMenuAction,
  useAeContextMenuItemMutation,
  useAssistantLoadingStore,
} from '@diana/core';
import { useFindStepsGroupModelItem } from '../../../Support/useFindStepsGroupModelItem';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';

export const AeCreateStepFooter = () => {
  const [isReloading, setIsReloading] = useState<boolean>(false);

  const findStepsGroupModelItem = useFindStepsGroupModelItem();

  const flowId = useEditorStore((s) => s.getFlow()?.id ?? '');
  const getFlow = useEditorStore((s) => s.getFlow);
  const setFlow = useEditorStore((s) => s.setFlow);
  const groupModelItem = useEditorStore((s) => findStepsGroupModelItem(s.model));
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const { isFetching: isFetchingModel } = useGetEditorModel({
    flowId: flowId ?? '',
    queryOptions: {
      refetchOnMount: true,
      enabled: !!flowId,
    },
  });

  const modelItemId = groupModelItem?.id ?? '';

  const { mutate, isPending } = useAeContextMenuItemMutation({
    modelItemId,
    onMutate: () => setIsReloading(true),
    onError: () => setIsReloading(false),
    onSuccess: (data) => {
      const flow = getFlow();

      const steps = Array.isArray(data)
        ? data
        : isPlainObject(data) && Array.isArray(data.steps)
        ? data.steps
        : null;

      const isStepObject = (step: any) => {
        return isPlainObject(step) && 'stepId' in step && 'taskId' in step && 'actionSetId' in step;
      };

      if (flow && steps?.every(isStepObject)) {
        setFlow({ ...flow, steps });
      }
    },
  });

  const addChild = groupModelItem?.addChild;

  const isLoading = isPending || isReloading;

  const disabled = hasAnyPendingStatus || isChatLoading || isLoading;

  useEffect(() => {
    if (!isFetchingModel) {
      setIsReloading(false);
    }
  }, [isFetchingModel]);

  if (!addChild) {
    return <Fragment />;
  }

  return (
    <Box py={0.5}>
      <Button
        sx={{ justifyContent: 'left', textTransform: 'none', height: 32 }}
        disabled={disabled}
        onClick={() =>
          mutate({
            method: 'POST',
            label: addChild.label,
            uri: addChild.uri,
            data: addChild.data,
            muiIconName: '',
            hasSubMenu: false,
            sequence: 0,
            action: ContextMenuAction.Create,
          })
        }
        startIcon={
          isLoading ? (
            <CircularProgress size="1em" />
          ) : (
            <AddIcon color={disabled ? 'secondary' : 'primary'} />
          )
        }
        fullWidth
      >
        {addChild.label}
      </Button>
    </Box>
  );
};
